import { useState } from "react";
import { RefreshCw, Volume2 } from "lucide-react";
import { replayGreeting, CHARACTER_META } from "@/lib/audio";

/**
 * Small elegant gold circular arrow — replays the character's greeting.
 * Every character-led page (/local, /demo, /leader, /moto, /bentley)
 * imports this next to the character name so users can re-trigger the
 * spoken line whenever they want.
 *
 * Props:
 *  - character: "alfred" | "bentley" | "amanda"
 *  - line: (optional) custom text to speak; defaults to the character's greeting
 *  - label: (optional) button label — defaults to "Replay {Character}"
 *  - variant: "default" | "compact"
 */
export default function ReplayGreeting({ character = "alfred", line, label, variant = "default", className = "" }) {
  const [spinning, setSpinning] = useState(false);
  const meta = CHARACTER_META[character] || CHARACTER_META.alfred;

  const handle = () => {
    setSpinning(true);
    if (line) {
      import("@/lib/audio").then(({ speakAs, unlockAudio }) => {
        unlockAudio();
        speakAs(character, line);
      });
    } else {
      replayGreeting(character);
    }
    setTimeout(() => setSpinning(false), 900);
  };

  const isCompact = variant === "compact";
  const buttonLabel = label || `Replay ${meta.name}`;

  return (
    <button
      type="button"
      onClick={handle}
      data-testid={`replay-${character}`}
      aria-label={buttonLabel}
      title={buttonLabel}
      className={`inline-flex items-center gap-1.5 rounded-sm border border-gold/50 text-gold hover:border-gold hover:bg-gold/10 transition-all ${
        isCompact
          ? "px-2 py-1 text-[10px]"
          : "px-3 py-1.5 text-[11px]"
      } font-mono-aviation uppercase tracking-[0.24em] min-h-[32px] ${className}`}
      style={{ boxShadow: "0 0 12px -4px rgba(212,175,55,0.4)" }}
    >
      <RefreshCw
        size={isCompact ? 10 : 12}
        className={spinning ? "animate-spin" : ""}
      />
      {!isCompact && (
        <>
          <Volume2 size={11} className="opacity-70" />
          <span>{buttonLabel}</span>
        </>
      )}
    </button>
  );
}
